import {
    Component, ComponentFactoryResolver, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, Type as AngularCoreType,
    ViewChild, AfterViewInit
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { CurrentUserService } from '../../../../security/current-user.service';
import { AccessibilityHelper } from '../../../accessibility.helper';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../dialogs/confirmation/confirmation-dialog.component';
import { IFileUploadProgressValueHash } from '../../../dialogs/file-upload/file-upload-dialog.component';
import { ConfirmationDialogEnums } from '../../../enums/confirmation-dialog.enum';
import { FormFieldProcessingPhaseEnum } from '../../../enums/form-field-processing-phase.enum';
import { FormFieldConfigurationSaveReason } from '../../../enums/form-field-save-config-reason.enum';
import { ItemTypeEnum } from '../../../enums/item-type.enum';
import { IGridRow } from '../../../interfaces/grid-row.interface';
import { FabricFileTypeIconMapping, IFileTypeIconMap } from '../../../mappings/fabric-file-types-icon.mapping';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { FormField } from '../../../models/form-builder/form-field.model';
import { FormInstanceElement } from '../../../models/form-builder/form-instance-element.model';
import { ComponentAndFormField } from '../../../models/grid/component-and-form-field-model';
import { GridConfig } from '../../../models/grid/grid-config.model';
import { RuntimeMetadata } from '../../../models/grid/runtime-metadata.model';
import { AttachmentService } from '../../../services/attachment.service';
import { CurrentSiteService } from '../../../services/current-site.service';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FormFieldTypeAndNameService } from '../../../services/form-field-type-and-name.service';
import { CachedFormFieldService } from '../../../services/form-field-with-cache.service';
import { FileUploaderComponent } from '../../file-uploader/file-uploader.component';
import { FormFieldConstraintLiaisonManager } from '../../form-builder/properties/form-field-properties/constraints/field-constraint-liaison-manager';
import { ButtonConfig, IButtonConfig } from '../../list-view/list-view-button-config.model';
import { TrashConfig, TrashDeleteEvent } from '../../list-view/list-view-trash-config.model';
import { ListViewComponent } from '../../list-view/list-view.component';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { GridFormFieldComponent } from '../grid-form-field/grid-form-field.component';
import { GridFieldEditorComponent } from '../grid-field-editor/grid-field-editor.component';
import { AttachmentStandardColumnNames, GridEventTypeEnum, GridFlavorEnum, GridFlavorModifiersEnum } from '../../../enums/grid.enums';
import { GridEvent } from '../grid-form-field/grid-event';
import { DeleteRestoreAttachmentDialog } from '../../../dialogs/delete-restore-attachment/delete-restore-attachment.dialog';
import { GridCellContextMenuInfo } from '../../../models/grid/cell-context-menu-info.model';
import { FormModeEnum } from '../../../enums/form-mode.enum';

@Component({
    selector: 'app-attachment-form-field',
    templateUrl: './attachment-form-field.component.html',
    styleUrls: ['./attachment-form-field.component.scss', '../form-fields.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AttachmentFormFieldComponent,
            multi: true
        }
    ],
    standalone: false
})
/* 
---------------------------------------------------------------------------------------------------------
    SEE https://community-dc.max.gov/x/qgY0mQ FOR AN OVERVIEW OF HOW ATTACHMENTS HAVE BEEN IMPLEMENTED
---------------------------------------------------------------------------------------------------------
*/
export class AttachmentFormFieldComponent extends FormFieldBaseComponent implements OnInit, AfterViewInit {

    @Output() onInit = new EventEmitter();
    @Output() onEdit = new EventEmitter();
    @Output() requestFormInstanceIds = new EventEmitter();
    @Output() designChange = new EventEmitter();

    @Input() selectedId: number;
    @Input() flavor: GridFlavorEnum = GridFlavorEnum.AttachmentsGrid;
    @Input() flavorModifier: GridFlavorModifiersEnum; // when the flavor is AttachmentsGrid, this can be 'showAll' or 'latestOnly'

    @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;
    @ViewChild(ListViewComponent) attachmentListComponent: ListViewComponent;
    @ViewChild(FileUploaderComponent) fileUploaderComponent: FileUploaderComponent;
    @ViewChild(MatMenuTrigger) gridCellContextMenu: MatMenuTrigger;

    private wrappedGridFormFieldComponent: GridFormFieldComponent;

    // Constructor.
    public constructor(private resolver: ComponentFactoryResolver,
        private hostElement: ElementRef,
        private renderer: Renderer2,
        private attachmentService: AttachmentService,
        private dialog: MatDialog,
        private cachedFormFieldConstraintsService: CachedFormFieldService,
        private currentSiteService: CurrentSiteService,
        private currentUserService: CurrentUserService,
        private dateAdapter: DateAdapter<Date>,
        private fieldDefinitionService: FieldDefinitionService,
        private formFieldTypeAndNameService: FormFieldTypeAndNameService
    ) {
        super();
    }

    // Life cycle methods.
    public ngOnInit(): void {

        // START ----------------------------- STUFF FOR ATTACHMENTS GRID ---------------------------
        // This component wraps a regular GridFormFieldComponent
        this.wrappedGridFormFieldComponent = new GridFormFieldComponent(
            this.resolver,
            this.hostElement,
            this.renderer,
            this.attachmentService,
            this.dialog,
            this.cachedFormFieldConstraintsService,
            this.currentSiteService,
            this.currentUserService,
            this.dateAdapter,
            this.fieldDefinitionService,
            this.formFieldTypeAndNameService
        );

        // Set Inputs 
        this.wrappedGridFormFieldComponent.formField = this.formField;
        this.wrappedGridFormFieldComponent.flavor = this.AttachmentsGridFlavor;
        this.wrappedGridFormFieldComponent.flavorModifier = GridFlavorModifiersEnum.ShowLatestOnly;
        this.wrappedGridFormFieldComponent.mode = this.mode;

        //***Note that the gridConfig is created during the following ngOnInit when gridFormFieldComponent.createGridConfig() is called
        this.wrappedGridFormFieldComponent.ngOnInit();

        // EVENT HANDLERS ~ because GridFieldDesigner and GridFieldEditor events are emitted through GridFormFieldComponent, we have to
        // subscribe to them here, rather than in the HTML
        this.wrappedGridFormFieldComponent.onEdit.subscribe(data => {
            this.onEdit.emit(data);
        });
        this.wrappedGridFormFieldComponent.designChange.subscribe(fieldDesignChangeInfo => {
            this.designChange.emit(fieldDesignChangeInfo);
        });
        // Make sure the component gets protected from accidental data loss - see  https://community-dc.max.gov/x/MI4ynw for details
        this.wrappedGridFormFieldComponent.flaggedAsDirty.subscribe(() => {
            this.FormInstanceElement.UserUpdatedData = true;
            this.FormInstanceElement.gridRowsValueHasChanged = true;
        });
        this.wrappedGridFormFieldComponent.flaggedAsClean.subscribe(() => {
            this.FormInstanceElement.UserUpdatedData = false;
            this.FormInstanceElement.gridRowsValueHasChanged = false;
        });
        // Subscribe to and pass along the wrapped grid's formFieldValidated event so it can handled by the parent component (FormFieldWrapper)
        this.wrappedGridFormFieldComponent.formFieldValidated.subscribe(status => {
            this.formFieldValidated.emit(status);
        });
        // END ------------------------------ STUFF FOR ATTACHMENTS GRID -------------------------------

        // Emit my onInit() output.
        let hshProperties = this.getProperties();
        this.onInit.emit(hshProperties);

        // Emit a request to get additional Id values.
        {
            let hshGetIdsInfo = {
                'formInstanceElement': this.FormInstanceElement
            };
            this.requestFormInstanceIds.emit(hshGetIdsInfo);

            // Save Id values.
            this.dataCollectionId = hshGetIdsInfo['dataCollectionId'];
            this.folderId = hshGetIdsInfo['folderId'];
            this.formInstanceId = hshGetIdsInfo['formInstanceId'];
        } // block

        //**Assign the value for the gridConfig
        this.gridConfig = this.wrappedGridFormFieldComponent.GridConfig;

        return;
    }

    public ngAfterViewInit(): void {
        //**Set the context menu in this event since this event guarantees that the view template has been initialized since
        //**this.gridCellContextMenu uses the @ViewChild decorator
        this.wrappedGridFormFieldComponent.gridCellContextMenu = this.gridCellContextMenu;
    }

    // START =============================================== PROPERTIES AND METHODS FOR NEW ATTACHMENTS =============================================
    @ViewChild(GridFieldEditorComponent, { read: GridFieldEditorComponent }) gridFieldEditorComponent: GridFieldEditorComponent;
    private gridConfig: GridConfig = null;
    private runtimeMetadata: RuntimeMetadata = new RuntimeMetadata();

    public get WrappedGridFormFieldComponent(): GridFormFieldComponent {
        return this.wrappedGridFormFieldComponent;
    }

    // Access wrappedGridFormFieldComponent's GridConfig and RuntimeMetadata
    public saveConfiguration(form: any, reasonForSave: FormFieldConfigurationSaveReason): void {
        // Cache form fields during the save configuration operation.
        // TODO: should I cache the rows here in Attachments Component instead? OR is it best to delegate to Wrapped Grid
        this.wrappedGridFormFieldComponent.GridConfig.CachedGridColumnDefs = this.wrappedGridFormFieldComponent.GridConfig.ColumnDefs;

        // Call form fields' saveConfiguration() method.
        let allComponentsAndFormFields: ComponentAndFormField[] = this.wrappedGridFormFieldComponent.RuntimeMetadata.AllComponents;
        for (let iComponent: number = 0; iComponent < allComponentsAndFormFields.length; iComponent++) {
            let component: FormFieldBaseComponent = allComponentsAndFormFields[iComponent].Component;

            component.saveConfiguration(form, reasonForSave);
        }

        // Save my column definitions as JSON text.
        let currentSite = this.currentSiteService.Site;
        this.FormField.childFormFields = [];

        for (let iCol: number = 0; iCol < this.wrappedGridFormFieldComponent.GridConfig.ColumnCount; iCol++) {
            let gridColDef: FormField = this.wrappedGridFormFieldComponent.GridConfig.getColumnDef(iCol);

            let childFormField: FormField = new FormField();

            childFormField.assignFrom(gridColDef);
            if (childFormField.id < 0)
                childFormField.id = 0;
            FormFieldConstraintLiaisonManager.synchronousUpdateFormFieldWithLatestConstraintValues(this.cachedFormFieldConstraintsService, currentSite.id, childFormField);

            this.FormField.childFormFields.push(childFormField);
        }

        return;
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public CheckFieldForValidation() {
        const statusChangeName = this.FieldHasValidationError ? FormFieldPropertyEnum.INVALID : FormFieldPropertyEnum.VALID;
        if (this.mode != FormModeEnum.DESIGN) {
            this.emitValidation(statusChangeName);
        }
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public get FieldHasValidationError() {
        const has = this.FormField.required && !this.gridFieldEditorComponent.HasRows;
        return has;
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public getValidationErrors(revealValidationErrors: boolean = false): string[] {
        if (this.FormField.required && !this.gridFieldEditorComponent.HasRows) {
            this.fieldHasValidationError = true;
            return [`${this.FormField.displayName || this.FormField.name} is required but has no rows`];
        } else {
            this.fieldHasValidationError = false;
            return [];
        }
    }

    public handleEditorGridValidation(event: any) {
        this.wrappedGridFormFieldComponent.mainGridValidation.emit(event);
    }

    public handleConditionalLogicColumnDefsFilter(event: any) {
        //console.log('AttachmentCOmp.handleConditionalLogicColumnDefsFilter($event)...');
    }

    public handleGridEvent(event: GridEvent) {
        let attachmentId = event.data.__gridRow?.databaseId;
        if (attachmentId) {
            if (event.type == GridEventTypeEnum.Download) {
                let url = this.attachmentService.viewFile(attachmentId);
                window.open(url, '_blank').focus();
            } else if (event.type == GridEventTypeEnum.Upload) {
                this.openUploadDialog(this.FormInstanceElement.id, attachmentId, event.data[AttachmentStandardColumnNames.Title]);
            } else if (event.type == GridEventTypeEnum.Delete) {
                this.openDeleteRestoreDialog(attachmentId, 'Delete');
            } else if (event.type == GridEventTypeEnum.Restore) {
                this.openDeleteRestoreDialog(attachmentId, 'Restore');
            }
        }
    }

    public get AttachmentsGridFlavor(): GridFlavorEnum {
        return GridFlavorEnum.AttachmentsGrid;
    }

    public get ToggleShowAllButtonText(): string {
        return this.wrappedGridFormFieldComponent.flavorModifier == GridFlavorModifiersEnum.ShowAll ?
            'Show Latest Only' :
            'Show All Versions';
    }

    public openUploadDialog(formInstanceElementId: number, attachmentId: number = 0, attachmentTitle: string = null): void {
        this.fileUploaderComponent.openUploadDialog(formInstanceElementId, attachmentId, attachmentTitle);
    }

    public openDeleteRestoreDialog(attachmentId: any, action: string) {
        let dialogRef = this.dialog.open(DeleteRestoreAttachmentDialog, {
            data: { attachmentId: attachmentId, deleteOrRestore: action }
        });

        dialogRef.afterClosed().subscribe(result => {
            this.gridFieldEditorComponent.rebind();
        });
    }

    public toggleShowAll() {
        let modifier = this.wrappedGridFormFieldComponent.flavorModifier == GridFlavorModifiersEnum.ShowLatestOnly ?
            GridFlavorModifiersEnum.ShowAll :
            GridFlavorModifiersEnum.ShowLatestOnly;
        this.wrappedGridFormFieldComponent.flavorModifier = modifier;
        this.wrappedGridFormFieldComponent.GridConfig.FlavorModifier = modifier;
        this.gridFieldEditorComponent.rebind();
    }

    // Called from Form Builder when properties drawer is closed
    public resetFormField(formField: FormField): void {
        this.wrappedGridFormFieldComponent.resetFormField(formField);
    }

    // END ============================================================== METHODS FOR NEW ATTACHMENTS ====================================================

    // Keep track of Id values needed for file uploads.
    private dataCollectionId: number = -1;
    private folderId: number = -1;
    private formInstanceId: number = -1;

    //TEAMS-424: KLW - Variable to determine if the upload button should be disabled
    private disableUploadButton = true;
    //TEAMS-424: KLW - Flag to determine if checkboxes should be displayed for the list views
    hasCheckboxes: boolean = true;
    hideActionButtons: boolean = true;

    buttonConfig: any;
    title = 'Attachments';

    private readonly formFieldProperties: string[] =
        [
            FormFieldPropertyEnum.NAME,
            FormFieldPropertyEnum.FIELD_GROUP,
            FormFieldPropertyEnum.REQUIRED,
            FormFieldPropertyEnum.DISPLAY_NAME,
            FormFieldPropertyEnum.HELP_TEXT,
            FormFieldPropertyEnum.PLACEHOLDER_TEXT,
            FormFieldPropertyEnum.TOOL_TIP,
            FormFieldPropertyEnum.INSTRUCTIONS_TEXT,
            FormFieldPropertyEnum.DISPLAY_KENDO_GRID_ROWS,
            FormFieldPropertyEnum.GRID_KEY_COLUMNS,
            FormFieldPropertyEnum.HIDE_GRID_FILTERING
        ];

    private readonly LIBRARY_ADD: string = 'library_add';
    private readonly STANDARD: string = 'standard';
    private readonly NONE: string = 'none';
    private readonly EDITABLE: string = 'Editable';
    private readonly CHECKED_OUT: string = 'Checked Out';
    private readonly MORE_VERT: string = 'more_vert';
    private readonly INFO: string = 'info';

    private contextMenuPosition = { x: '0px', y: '0px' };

    private _fileTypeIconMap: IFileTypeIconMap[] = null;

    private selectedItemDescription: string;
    private selectedItemDisplayOrder: number;

    private get FileTypeIconMap(): IFileTypeIconMap[] {
        if (this._fileTypeIconMap == null) {
            this._fileTypeIconMap = new FabricFileTypeIconMapping().FileTypeIconMap;
        }

        return this._fileTypeIconMap;
    }

    public get DataCollectionId(): number {
        return (this.dataCollectionId);
    }

    public get FolderId(): number {
        return (this.folderId);
    }

    public get FormInstanceId(): number {
        return (this.formInstanceId);
    }

    public get FormFieldId(): number {
        return (this.FormField.id);
    }

    public get ContextMenuPositionX(): string {
        let menuData: GridCellContextMenuInfo = (this.gridCellContextMenu && this.gridCellContextMenu.menuData ? <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);
        return (menuData ? menuData.contextMenuXPos : '');

        //return (this.contextMenuPosition.x);
    }
    public get ContextMenuPositionY(): string {
        let menuData: GridCellContextMenuInfo = (this.gridCellContextMenu && this.gridCellContextMenu.menuData ? <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);
        return (menuData ? menuData.contextMenuYPos : '');

        //return (this.contextMenuPosition.y);
    }
    private _enableAttachments = true;

    private _editInputAppearance: boolean = false;
    public get EditInputAppearance() {
        if (this._editInputAppearance)
            return this.STANDARD;
        else
            return this.NONE;
    }

    private _editEnabled: boolean = false;
    public get EditEnabled() {
        return this._editEnabled;
    }

    public selectedItemFileName = '';
    public get EditFileName() {
        if (!this._editEnabled)
            return this.selectedItemFileName;
        else
            return this.selectedItemFileName.split('.')[0];
    }

    public get SelectedItemDescription(): string {
        return this.selectedItemDescription;
    }

    public set SelectedItemDescription(value: string) {
        this.selectedItemDescription = value;
    }

    private buildButtonConfig(): void {

        // Construct the button configs array.
        let itemActions: IButtonConfig[] =
            [
                new ButtonConfig('View Details', this.INFO, this.INFO),
                new ButtonConfig('Sub Menu', this.MORE_VERT, this.MORE_VERT)
            ];

        this.buttonConfig = {
            right: [
                // 11/5/2024 ~ Removing the add button. Old attachments should be displayed for reference but not added to
                // new ButtonConfig('Add Attachment', this.LIBRARY_ADD, this.LIBRARY_ADD, IconFormatEnum.MAT_ICON, this.disableUploadButton) //TEAMS-424: KLW - Use new constructor to pass in if upload button should be disabled
            ],
            item_actions: itemActions,
            trash: new TrashConfig(true)
        };
    }


    private deleteOnConfirm(entity: string[], callback: Function) {

        let title = 'Delete Attachment';
        let desc = 'Are you sure you want to delete the attachment';

        if (entity.length > 1) {
            title += 's';
            desc += 's:\n';

            entity.forEach(x => {
                desc += x + '\n';
            });
        }
        else {
            desc += ' ' + entity[0] + '?\n';
        }

        desc += '\nThe operation cannot be undone!';

        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(title, desc, { hideNo: false, dialogType: ConfirmationDialogEnums.WARNING }),
            panelClass: 'dialog-confirm'
        });

        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
        });
    }

    private getAssociatedFabricIconProperties(extension: string): IFileTypeIconMap {
        let retVal: IFileTypeIconMap = null;

        if (extension) {
            let findKey = this.FileTypeIconMap.find(x => x.value.extensions.find(y => y == extension));

            if (findKey) {
                retVal = findKey;
            }
        }

        return retVal;
    }

    private getFileExtension(fileName: string): string {
        let retVal = '';

        if (fileName)
            retVal = fileName.split('.').pop();

        return retVal;
    }


    public canPreview(fileName: string): boolean {
        let retVal: boolean = false;

        let fileType = this.getAssociatedFabricIconProperties(this.getFileExtension(fileName));

        if (fileType)
            if (fileType.value.canpreview)
                retVal = true;

        return retVal;
    }

    public EditButtonClicked() {
        this._editEnabled = !this._editEnabled;
        this._editInputAppearance = !this._editInputAppearance;
    }

    public deselectAttachment() {
        this._editInputAppearance = false;
        this._editEnabled = false;
        this.selectedItemFileName = '';
        this.selectedItemDescription = '';
        this.selectedItemDisplayOrder = 0;
    }

    public cancelClicked(): void {
        this.deselectAttachment();
    }

    // Implement abstract methods.
    public getProperties(): any {
        let hshProperties =
        {
            component: this,
            formField: this.FormField,
            properties: this.formFieldProperties
        };

        return (hshProperties);
    }

    public get ActivityListIcon(): string {
        return ItemTypeEnum.ATTACHMENT_ACTIVITY;
    }

    public formatBytes(bytes, decimals = 0.0) {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    // Called to initialize/re-initialize the component and its FormControl (formerly named writeValueTriggered)
    protected initalizeComponentFormControl(): void {
        if (this.IsInPreviewOrInstanceMode) {
            // Ask my data source to deserialize any JSON grid data (might not be any).
            if (this.FormInstanceElement.childGridRows) {
                this.wrappedGridFormFieldComponent.createConfigFromChildGridRows(this.FormInstanceElement.childGridRows);
            }
            if (this.gridFieldEditorComponent != null) {
                this.gridFieldEditorComponent.parentReceivedFormInstanceElement(this.FormInstanceElement);
            }
        } // if 

        return;
    }


    // Override the getDisplayValue() base class method.
    // Define a method that allows a component to return its display value.
    public pseudoStatic_getDisplayValue(formFieldParam: FormField,
        formInstanceElementParam: FormInstanceElement,
        gridRow: IGridRow,
        processingPhase: FormFieldProcessingPhaseEnum): string {
        //if ((!formInstanceElementParam.transientValueSetFlag) ||
        if ((!formInstanceElementParam.UserUpdatedData) ||
            (!formInstanceElementParam.textValue)) {
            // Set a default value.
            formInstanceElementParam.TextValue = '';
        }

        // NOTE:  NEED TO REVISIT THIS.
        return (formInstanceElementParam.textValue);
    }

    // Override a method used to get my class.
    public getFormFieldClass(): AngularCoreType<any> {
        return (AttachmentFormFieldComponent);
    }

    // Handle control events.
    public handleFilesUploaded(eventData: IFileUploadProgressValueHash): void {
        if (eventData) {
            this.gridFieldEditorComponent.rebind();
        }
    }

    //VNEXT-707: KLW - Add option for Edit in App for SharePoint attachments
    public openEditInAppUrl(): void {
        let editInAppUrl = this.contextMenu.menuData.item.editInAppUrl;

        if (editInAppUrl)
            window.open(editInAppUrl, '_blank').focus();
    }

    public openEditInBrowserUrl(): void {
        let editInBrowserUrl = this.contextMenu.menuData.item.editInBrowserUrl;

        if (editInBrowserUrl)
            window.open(editInBrowserUrl, '_blank').focus();
    }

    public openViewInBrowserUrl(): void {
        let viewInBrowserUrl = this.contextMenu.menuData.item.viewInBrowserUrl;

        if (viewInBrowserUrl)
            window.open(viewInBrowserUrl, '_blank').focus();
    }

    public openDownloadUrl(event: any = null): void {
        let item;
        if (event) {
            item = event.item;
        } else {
            item = this.contextMenu.menuData.item;
        }
        let downloadUrl = item.downloadUrl;

        if (downloadUrl)
            window.open(downloadUrl, '_blank').focus();
    }

    // DEPRECATED
    public doOnButtonClick(eventData: any): void {
        if (eventData.button == this.LIBRARY_ADD) {
            this.fileUploaderComponent.openUploadDialog(this.FormInstanceElement.id);
        }
    }

    /**
     * Event which is called when a user chooses an option from the context menu for a grid cell's field.
     * @param eventData - Contains the data associated with the click event that occurred when user chose the context option.
     * @param menuItemName - Contains the context menu item name that was chosen
     */
    public gridCellContextMenuItemClicked(eventData: any, menuItemName: string): void {
        // Get my menu data.
        let menuData: GridCellContextMenuInfo = 
            (this.gridCellContextMenu && this.gridCellContextMenu.menuData ?
                <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);

        let bSuccess: boolean = false;

        switch (menuItemName) {
            case 'Properties ...':
                {
                    if (menuData && menuData.component) {
                        let hshComponentProperties: any = menuData.component.getProperties();

                        hshComponentProperties['component'] = this;

                        this.onEdit.emit(hshComponentProperties);

                        bSuccess = true;
                    }
                }
                break;

            case 'Delete':
                {
                    if (menuData && menuData.columnDef) {
                        let gridConfigColDef: FormField = this.gridConfig.getColumnDefByName(menuData.columnDef.name);

                        if (gridConfigColDef) {
                            bSuccess = this.gridConfig.removeGridColumn(gridConfigColDef.gridColClientId);
                            if (bSuccess) {
                                //***Signal that page isDirty. This will emit to formFieldWrapperComponent
                                this.designChange.emit();
                            }
                                
                            
                        }
                    }
                }
                break;

            default:
                // Unknown menu item.
                break;
        }

        return;
    }
}






